import { addMonths, endOfMonth, format, isMonday, parseISO, startOfMonth, subDays } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { AccountEntry, AccountStatus } from './types'

export const money = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

export function formatMoney(value: number) {
  return money.format(Number(value || 0))
}

export function parseMoneyInput(value: string) {
  const cents = value.replace(/\D/g, '')
  return cents ? Number(cents) / 100 : 0
}

export function formatMoneyInput(value: number) {
  if (!value) return ''
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
}

export function formatDate(value: string) {
  if (!value) return '—'
  return format(parseISO(`${value.slice(0, 10)}T12:00:00`), 'dd/MM/yyyy', { locale: ptBR })
}

export function todayIso() {
  return format(new Date(), 'yyyy-MM-dd')
}

export function accountStatus(entry: Pick<AccountEntry, 'paid_at' | 'due_date'>): AccountStatus {
  if (entry.paid_at) return 'PAGO'
  return entry.due_date <= todayIso() ? 'VENCIDO' : 'A VENCER'
}

export function monthIso(date: Date) {
  return format(startOfMonth(date), 'yyyy-MM-dd')
}

export function monthLabel(value: string) {
  return format(parseISO(`${value.slice(0, 7)}-01T12:00:00`), 'MM/yyyy')
}

export function nextBillingMonth() {
  return monthIso(addMonths(new Date(), 1))
}

export function addMonthClamped(dateValue: string, months: number, preferredDay?: number) {
  const original = parseISO(`${dateValue}T12:00:00`)
  const target = addMonths(startOfMonth(original), months)
  const day = Math.min(preferredDay ?? original.getDate(), endOfMonth(target).getDate())
  return format(new Date(target.getFullYear(), target.getMonth(), day, 12), 'yyyy-MM-dd')
}

export function splitInstallments(total: number, count: number) {
  const cents = Math.round(total * 100)
  const base = Math.floor(cents / count)
  const remainder = cents - base * count
  return Array.from({ length: count }, (_, index) => (base + (index < remainder ? 1 : 0)) / 100)
}

export function dashboardRanges(now = new Date()) {
  const jsDay = now.getDay()
  const daysSinceSaturday = (jsDay + 1) % 7
  const weekStart = subDays(now, daysSinceSaturday)
  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekStart.getDate() + 6)
  const nextStart = new Date(weekStart)
  nextStart.setDate(weekStart.getDate() + 7)
  const nextEnd = new Date(nextStart)
  nextEnd.setDate(nextStart.getDate() + 6)
  return {
    today: { start: format(isMonday(now) ? subDays(now, 2) : now, 'yyyy-MM-dd'), end: format(now, 'yyyy-MM-dd') },
    week: { start: format(weekStart, 'yyyy-MM-dd'), end: format(weekEnd, 'yyyy-MM-dd') },
    next: { start: format(nextStart, 'yyyy-MM-dd'), end: format(nextEnd, 'yyyy-MM-dd') },
  }
}
